import { useContext, useState } from 'react';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';

import { AuthContext } from '../contexts/AuthContext';
import { LabourerContext } from '../contexts/LabourerContext';
import SectionHeader from './SectionHeader';

import GeneralInformation from './UserProfile/GeneralInformation';
import ContactDetails from './UserProfile/ContactDetails';
import EmergencyContact from './UserProfile/EmergencyContact';
import SuperAnnuationAndTax from './UserProfile/SuperAnnuationAndTax';
import BankDetails from './UserProfile/BankDetails';
import OnboardingLicenses from './LabourerOnboarding/OnboardingLicenses';
import OnboardingDocuments from './LabourerOnboarding/OnboardingDocuments';
import OnboardingPasswordReset from './LabourerOnboarding/OnboardingPasswordReset';
import OnboardingResume from './LabourerOnboarding/OnboardingResume';

import labourerService from '../services/labourerService';
import authService from '../services/authService';

import { Squares2X2Icon, LifebuoyIcon, BanknotesIcon, BuildingLibraryIcon, PhoneIcon, LockClosedIcon, IdentificationIcon, DocumentCheckIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { ArrowLeftEndOnRectangleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';

import AseLogo from '../assets/ase-logo.png';

const StepIcon = ({ step }) => {
  switch (step) {
    case 'general-information':
      return <Squares2X2Icon className='h-5 w-5 text-ase-blue' />;
    case 'emergency-contact':
      return <LifebuoyIcon className='h-5 w-5 text-ase-blue' />;
    case 'contact-details':
      return <PhoneIcon className='h-5 w-5 text-ase-blue' />;
    case 'password-reset':
      return <LockClosedIcon className='h-5 w-5 text-ase-blue' />;
    case 'super-annuation-tax':
      return <BanknotesIcon className='h-5 w-5 text-ase-blue' />;
    case 'bank-details':
      return <BuildingLibraryIcon className='h-5 w-5 text-ase-blue' />;
    case 'licenses':
      return <DocumentCheckIcon className='h-5 w-5 text-ase-blue' />;
    case 'documents':
      return <IdentificationIcon className='h-5 w-5 text-ase-blue' />;
    case 'resume':
      return <DocumentTextIcon className='h-5 w-5 text-ase-blue' />;
    default:
      return <Squares2X2Icon className='h-5 w-5 text-ase-blue' />;
  }
}

const LabourerOnboarding = () => {
  const { mutateUser, setToken } = useContext(AuthContext);
  const { labourer, mutateLabourer } = useContext(LabourerContext);

  const [isLoading, setIsLoading] = useState(false);

  const isGeneralInformationCompleted = Boolean(labourer?.user?.fullName && labourer?.user?.preferredName && labourer?.user?.dateOfBirth);
  const isContactDetailsCompleted = Boolean(labourer?.user?.email && labourer?.user?.residentialAddress && labourer?.user?.phoneNumber);
  const isEmergencyContactCompleted = Boolean(labourer?.emergencyContact?.fullName && labourer?.emergencyContact?.relationship && labourer?.emergencyContact?.phoneNumber);
  const isSuperAnnuationAndTaxCompleted = Boolean(labourer?.superAnnuation?.fundName && labourer?.superAnnuation?.ABN && labourer?.superAnnuation?.USI && labourer?.taxFileNumber);
  const isBankDetailsCompleted = Boolean(labourer?.bankDetails?.bankName && labourer?.bankDetails?.BSB && labourer?.bankDetails?.accountNumber && labourer?.bankDetails?.accountName);

  const steps = [
    {
      id: 'general-information',
      label: 'General information',
      completed: isGeneralInformationCompleted
    },
    {
      id: 'contact-details',
      label: 'Contact details',
      completed: isContactDetailsCompleted
    },
    {
      id: 'emergency-contact',
      label: 'Emergency contact',
      completed: isEmergencyContactCompleted
    },
    {
      id: 'password-reset',
      label: 'Password reset',
      completed: labourer?.user?.hasCompletedPasswordReset
    },
    {
      id: 'super-annuation-tax',
      label: 'Super Annuation & Tax',
      completed: isSuperAnnuationAndTaxCompleted
    },
    {
      id: 'bank-details',
      label: 'Bank details',
      completed: isBankDetailsCompleted
    },
    {
      id: 'licenses',
      label: 'Licenses',
      completed: true
    },
    {
      id: 'documents',
      label: 'Documents',
      completed: true
    },
    {
      id: 'resume',
      label: 'Resume',
      completed: true
    }
  ];

  const [currentStep, setCurrentStep] = useState(steps.find(s => s.id === 'general-information'));

  const personalSteps = ['general-information', 'contact-details', 'password-reset', 'emergency-contact'];
  const financeSteps = ['super-annuation-tax', 'bank-details'];
  const complianceSteps = ['licenses', 'documents', 'resume'];

  const handleGeneralInformationSubmit = async (data) => {
    try {
      setIsLoading(true);

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          ...data
        }
      });

      await mutateLabourer();

      setIsLoading(false);
      setCurrentStep(steps.find(s => s.id === 'contact-details'));
      toast.success('General information updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    }
  }

  const handleContactDetailsSubmit = async (data) => {
    try {
      setIsLoading(true);

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          ...data
        }
      });

      await mutateLabourer();

      setCurrentStep(steps.find(s => s.id === 'emergency-contact'));
      toast.success('Contact details updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handleEmergencyContactSubmit = async (data) => {
    try {
      setIsLoading(true);

      const { fullName, relationship, phoneNumber } = data;

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          emergencyContact: {
            fullName,
            relationship,
            phoneNumber
          }
        }
      });

      await mutateLabourer();

      setIsLoading(false);
      setCurrentStep(steps.find(s => s.id === 'password-reset'));
      toast.success('General information updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    }
  }

  const handleSuperAnnuationAndTaxSubmit = async (data) => {
    try {
      setIsLoading(true);

      const { superAnnuation, taxFileNumber } = data;

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          superAnnuation,
          taxFileNumber
        }
      });

      mutateLabourer();
      setIsLoading(false);
      setCurrentStep(steps.find(s => s.id === 'bank-details'));
      toast.success('Super Annuation & Tax updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    }
  }

  const handleBankDetailsSubmit = async (data) => {
    try {
      setIsLoading(true);

      const { bankName, BSB, accountNumber, accountName } = data;

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          bankDetails: {
            bankName,
            BSB,
            accountNumber,
            accountName
          }
        }
      });

      await mutateLabourer();

      setIsLoading(false);
      setCurrentStep(steps.find(s => s.id === 'licenses'));
      toast.success('Bank details updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    }
  }

  const handleResetPasswordSubmit = async ({ newPassword }) => {
    try {
      setIsLoading(true);

      await authService.updatePassword({
        userId: labourer.user._id,
        password: newPassword
      });

      await mutateLabourer();

      setIsLoading(false);

      setCurrentStep(steps.find(s => s.id === 'super-annuation-tax'));
      toast.success('Bank details updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    }
  }

  const onRequiredLicensesSubmit = async () => {
    setCurrentStep(steps.find(s => s.id === 'documents'));
  }

  const onRequiredDocumentsSubmit = async () => {
    setCurrentStep(steps.find(s => s.id === 'resume'));
  }

  const onRequiredResumeSubmit = async () => {
    if (isGeneralInformationCompleted && isContactDetailsCompleted && isEmergencyContactCompleted && isSuperAnnuationAndTaxCompleted && isBankDetailsCompleted) {
      try {
        setIsLoading(true);

        await labourerService.updateLabourer({
          labourerId: labourer._id,
          updateData: {
            completedRegistration: true
          }
        });

        await mutateLabourer();

        toast.success('Onboarding completed');

        setIsLoading(false);
      } catch (error) {
        console.log(error);
        toast.error('Something went wrong');
      }
    } else {
      toast.error('Please complete all steps before submitting');
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    mutateUser(null);
    mutateLabourer(null);
  }

  return (
    <div className='flex flex-col'>
      <Helmet>
        <title>Onboarding | ASE Group</title>
      </Helmet>

      <img src={AseLogo} alt='ASE Group' className='h-[40px] mx-auto mb-4' />
      <h1 className='mb-2 text-center text-lg font-semibold'>Hey {labourer?.user?.preferredName || labourer?.user?.fullName}, welcome to ASE Group!</h1>
      <p className='mb-4 md:mb-8 text-center'>Please fill in the form to start receiving work.</p>

      <div className='flex flex-col md:flex-row justify-between w-full md:w-[1000px] mx-auto mb-10 gap-4 md:gap-8'>
        <div className='flex gap-4 bg-ase-blue md:hidden overflow-scroll p-2 rounded-md'>
          {steps.map(step => (
            <button
              onClick={() => setCurrentStep(step)}
              key={step.id}
              className={`${currentStep.id === step.id ? 'bg-white text-ase-blue' : 'bg-white text-black bg-opacity-65'} px-2 flex items-center justify-between gap-2 hover:text-ase-blue py-2 text-xs w-full rounded-md text-nowrap`}
            >
              <div className='flex items-center gap-2'>
                <StepIcon step={step.id} />
                <span>{step.label}</span>

                {step.completed
                  ? <CheckCircleIcon className='h-[20px] text-green-500' />
                  : <XCircleIcon className='h-[20px] text-red-500' />}
              </div>
            </button>
          ))}
        </div>

        <div className='hidden md:flex flex-col gap-4'>
          <div className='border-ase-blue border-x border-t rounded-lg h-fit w-[250px]'>
            <SectionHeader title='Personal' />

            <div className='bg-white p-2 border-b border-ase-blue rounded-b-lg'>
              {steps.filter(s => personalSteps.includes(s.id)).map(step => (
                <button
                  onClick={() => setCurrentStep(step)}
                  key={step.id}
                  className={`${currentStep.id === step.id ? 'bg-ase-blue text-ase-blue bg-opacity-10' : 'text-black'} px-2 flex items-center justify-between gap-2 hover:text-ase-blue py-2 text-sm w-full rounded-md`}
                >
                  <div className='flex items-center gap-2'>
                    <StepIcon step={step.id} />
                    <span>{step.label}</span>
                  </div>

                  {step.completed
                    ? <CheckCircleIcon className='h-[20px] text-green-500' />
                    : <XCircleIcon className='h-[20px] text-red-500' />}
                </button>
              ))}
            </div>
          </div>

          <div className='border-ase-blue border-x border-t rounded-lg h-fit w-[250px]'>
            <SectionHeader title='Finance' />

            <div className='bg-white p-2 border-b border-ase-blue rounded-b-lg'>
              {steps.filter(s => financeSteps.includes(s.id)).map(step => (
                <button
                  onClick={() => setCurrentStep(step)}
                  key={step.id}
                  className={`${currentStep.id === step.id ? 'bg-ase-blue text-ase-blue bg-opacity-10' : 'text-black'} px-2 flex items-center justify-between gap-2 hover:text-ase-blue py-2 text-sm w-full rounded-md`}
                >
                  <div className='flex items-center gap-2'>
                    <StepIcon step={step.id} />
                    <span>{step.label}</span>
                  </div>

                  {step.completed
                    ? <CheckCircleIcon className='h-[20px] text-green-500' />
                    : <XCircleIcon className='h-[20px] text-red-500' />}
                </button>
              ))}
            </div>
          </div>

          <div className='border-ase-blue border-x border-t rounded-lg h-fit w-[250px]'>
            <SectionHeader title='Compliance' />

            <div className='bg-white p-2 border-b border-ase-blue rounded-b-lg'>
              {steps.filter(s => complianceSteps.includes(s.id)).map(step => (
                <button
                  onClick={() => setCurrentStep(step)}
                  key={step.id}
                  className={`${currentStep.id === step.id ? 'bg-ase-blue text-ase-blue bg-opacity-10' : 'text-black'} px-2 flex items-center justify-between gap-2 hover:text-ase-blue py-2 text-sm w-full rounded-md`}
                >
                  <div className='flex items-center gap-2'>
                    <StepIcon step={step.id} />
                    <span>{step.label}</span>
                  </div>

                  {step.completed
                    ? <CheckCircleIcon className='h-[20px] text-green-500' />
                    : <XCircleIcon className='h-[20px] text-red-500' />}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleLogout}
            className='bg-red-600 text-white font-semibold h-[36px] rounded-md w-[250px] flex items-center justify-center gap-1 text-sm'
          >
            <ArrowLeftEndOnRectangleIcon className='h-5 w-5' /> Sign out
          </button>
        </div>

        <div className='border border-ase-blue rounded-lg h-fit flex-grow bg-white pb-1'>
          <SectionHeader title={currentStep.label} />

          <div className='p-4'>
            {currentStep.id === 'general-information' && <GeneralInformation
              isLoading={isLoading}
              onSubmit={handleGeneralInformationSubmit}
            />}

            {currentStep.id === 'contact-details' && <ContactDetails
              isLoading={isLoading}
              onSubmit={handleContactDetailsSubmit}
            />}

            {currentStep.id === 'emergency-contact' && <EmergencyContact
              labourer={labourer}
              isLoading={isLoading}
              onSubmit={handleEmergencyContactSubmit}
              onBack={() => setCurrentStep('general-information')}
            />}

            {currentStep.id === 'password-reset' && <OnboardingPasswordReset
              isLoading={isLoading}
              onSubmit={handleResetPasswordSubmit}
            />}

            {currentStep.id === 'super-annuation-tax' && <SuperAnnuationAndTax
              labourer={labourer}
              isLoading={isLoading}
              onSubmit={handleSuperAnnuationAndTaxSubmit}
              onBack={() => setCurrentStep('emergency-contact')}
            />}

            {currentStep.id === 'bank-details' && <BankDetails
              labourer={labourer}
              isLoading={isLoading}
              onSubmit={handleBankDetailsSubmit}
              onBack={() => setCurrentStep('super-annuation-tax')}
            />}

            {currentStep.id === 'licenses' && <OnboardingLicenses
              onSubmit={onRequiredLicensesSubmit}
            />}

            {currentStep.id === 'documents' && <OnboardingDocuments
              isLoading={isLoading}
              onSubmit={onRequiredDocumentsSubmit}
            />}

            {currentStep.id === 'resume' && <OnboardingResume
              isLoading={isLoading}
              onSubmit={onRequiredResumeSubmit}
            />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LabourerOnboarding;
